import React, { useEffect, useMemo, useState, useRef } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router";

import {
  FILTER_LABEL,
  emptyString,
  pathConstant,
  loginViewConstants,
  brandProfileConstants,
  averageCardTitleConstants,
  performanceScreensConstants,
  AVERAGE_RATING_RESPONSE_TYPE,
  individualStorePerformanceConstants,
  INDIVIDUAL_STORE_PERFORMANCE_EXPORT_HEADERS,
  brandPerformanceConstants,
  PERFORMANCE_FILTER_DEFAULT_MIN_STAR,
  PERFORMANCE_FILTER_INITIAL_MAX_STAR,
  PERFORMANCE_EXPORT_CSV_DELAY_MS,
  PERFORMANCE_STATUS_LABELS,
  PERFORMANCE_DEFAULT_SORTING,
} from "../../constants";
import { setItemToStorage, getItemFromStorage } from "../../../../system";
import {
  useSpinner,
  Spinner,
  fileName,
  isInclude,
  DataTable,
  StatusCard,
  ExportButton,
  SearchComponent,
  UTCToDateFormat,
  AverageRatingCard,
  escapeSearchQuery,
  isSuperOrBrandAdmin,
  convertToDateToUnix,
  convertFromDateToUnix,
  datePreferenceUtility,
  useRatingCheckboxGroup,
  UTCToDateTimeFormatWithoutSeconds,
  openConversationAllInternalNotesModal,
  guestSentimentPaginatedPostRequestBody,
  handleAsyncAction,
} from "../../../../shared";
import {
  brandPerformanceActions,
  individualStoreProfileActions,
  individualStorePerformanceActions,
} from "../../redux/actions";
import FilterComponent from "./filterComponent";
import useModalDisplay from "../../../../shared/components/hooks/useModalDisplay/useModalDisplay";
import { isInboxActive } from "../../../../shared/components/sidebarContainer/utility";
import { individualStorePerformanceColumns } from "./utility";
import {
  buildSurveyCompletionDateFilterQuery,
  buildDatedPerformanceRequestPayload,
  getStarResponsesStatusTitle,
} from "../shared/performanceQueryUtility";

const { ALL_USER_RESPONSE_DATA, RESTAURANT_EXTERNAL_ID } =
  individualStorePerformanceConstants;
const { GET_AVERAGE_RATING_RESPONSES } = brandPerformanceConstants;
const { AVERAGE_TRANSACTION_RATING, AVERAGE_SENTIMENT_RATING } =
  averageCardTitleConstants;
const { GUEST_SENTIMENTS, GUEST_TRANSACTION } = performanceScreensConstants;

const componentName = "Consumer Responses";

//Displaying the general data of a brand and its survey responses in charts
const IndividualStorePerformance = (props) => {
  const {
    individualStorePerformanceReducer: {
      userResponsesData,
      allItemsForExport,
      allUserResponsesData,
      userResponseDataPagination,
      storesAvgSentimentAndTransactionRatingData,
    },
    authentication: { user },
    systemReducer: { allBrandsWithFeatureFlags },
    profileData,
    resetAllExportItems,
    getAllUserResponses,
    getStoreProfileData,
    getAverageRatingResponses,
    getAllUserResponsesItemsForExport,
  } = props;

  const { totalResponses, totalCustomers } = allUserResponsesData;

  const navigate = useNavigate();
  const location = useLocation();
  const { brandId, managerId, groupId, locationId } = useParams();

  const frRoute = getItemFromStorage(loginViewConstants.FR_ROUTE);

  const csvLinkRef = useRef();
  const [minStar, setMinStar] = useState(PERFORMANCE_FILTER_DEFAULT_MIN_STAR);
  const [maxStar, setMaxStar] = useState(PERFORMANCE_FILTER_INITIAL_MAX_STAR);
  const [starValue, setStarValue] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [queryString, setQueryString] = useState(emptyString);
  const [searchString, setSearchString] = useState(emptyString);
  const [sorting, setSorting] = useState(PERFORMANCE_DEFAULT_SORTING);
  const [selectedOption, setSelectedOption] = useState([]);
  const [dateState, setDateState] = useState(datePreferenceUtility());
  const [dayCounts, setDayCounts] = useState(emptyString);
  const [showModal, modalElement, setComponent, closeModal] = useModalDisplay();

  const createDateFrom = convertFromDateToUnix(dateState.startDate);
  const createDateTo = convertToDateToUnix(dateState.endDate);

  const { handleRatingOptionChange, selectedRatingOption } =
    useRatingCheckboxGroup(selectedOption, setStarValue, setSelectedOption);

  const isBrandInboxActive = isInboxActive(
    allBrandsWithFeatureFlags,
    isSuperOrBrandAdmin() ? brandId : user.brandId,
  );

  // Show loading while a request is running.
  const spinner = useSpinner();

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy, size) => {
    const averageSentiments = {
      min: minStar,
      max: maxStar,
    };

    const requestBody = guestSentimentPaginatedPostRequestBody({
      pagination: userResponseDataPagination,
      pageNumber,
      sortBy: sortBy || sorting,
      filterQuery: queryString,
      averageSentiments,
      size,
    });
    if (sortBy) {
      setSorting(sortBy);
    }

    return {
      ...requestBody,
      isGuestSentimentFilter: selectedRatingOption === GUEST_SENTIMENTS,
    };
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy, size) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy, size);

    if (
      (isInclude(requestBody.filter, GUEST_SENTIMENTS) &&
        selectedRatingOption === GUEST_SENTIMENTS) ||
      (isInclude(requestBody.filter, GUEST_TRANSACTION) &&
        selectedRatingOption === GUEST_TRANSACTION) ||
      !(
        isInclude(requestBody.filter, GUEST_TRANSACTION) ||
        isInclude(requestBody.filter, GUEST_SENTIMENTS)
      )
    ) {
      const escapedSearchQuery = escapeSearchQuery(searchString);
      getAllUserResponses(
        { ...requestBody, search: escapedSearchQuery, isFrRoute: false },
        locationId,
        frRoute,
        createDateFrom,
        createDateTo,
      );
      const { GET_INDIVIDUAL_STORE_AVERAGE_RATING_RESPONSE } =
        AVERAGE_RATING_RESPONSE_TYPE;
      getAverageRatingResponses(
        buildDatedPerformanceRequestPayload({
          createDateFrom,
          createDateTo,
          id: locationId,
          type: GET_INDIVIDUAL_STORE_AVERAGE_RATING_RESPONSE,
          requestBody: {
            ...requestBody,
            isFrRoute: !!frRoute,
            search: escapedSearchQuery,
          },
        }),
      );
    }
  };

  const navigateToConsumerResponses = (e) => {
    setItemToStorage(RESTAURANT_EXTERNAL_ID, e.restaurantId);
    const localLocationState = JSON.parse(
      getItemFromStorage(brandProfileConstants.LOCATION_STATE),
    );

    let path = `store-performance/${locationId}/consumer-responses/${e.customerId}`;

    let state = location.state;

    switch (state?.name || localLocationState?.name) {
      case pathConstant.STORES:
        path = `/stores/${brandId}/${path}`;
        break;
      case pathConstant.STORE_BRAND_PROFILE:
      case pathConstant.BRAND_PROFILE:
        path = `/stores/brand-profile/${brandId}/${path}`;
        break;
      case pathConstant.STORE_GROUP:
      case pathConstant.GROUP:
        path = `/stores/brand-groups/${brandId}/${path}`;
        break;
      case pathConstant.STORE_PERFORMANCE:
      case pathConstant.PERFORMANCE:
        path = `/stores/dashboard/${brandId}/brand-performance/${path}`;
        break;
      case pathConstant.GROUP_PERFROMANCE:
        path = `/dashboard/${groupId}/group-performance/${path}`;
        break;
      case pathConstant.GROUP_PROFILE:
        path = `/group-profile/${groupId}/${path}`;
        break;
      case pathConstant.MANAGER_PROFILE:
        path = `/manager-profile/${managerId}/${path}`;
        break;
      case pathConstant.MANAGER_PERFORMANCE:
        path = `/dashboard/${managerId}/manager-performance/${path}`;
        break;
      case pathConstant.GROUP_MANAGER:
        path = `/group-manager/${groupId}/${path}`;
        break;
      default:
        path = "";
        state = null;
    }

    navigate(path, { state });
  };

  //Used to export CSV
  const exportCSV = () => {
    setTimeout(() => {
      csvLinkRef.current?.link.click();
    }, PERFORMANCE_EXPORT_CSV_DELAY_MS);
  };

  //Get all data for export
  const getExportData = () => {
    const requestBody = getPaginatedPostRequestBody(
      userResponseDataPagination.number,
      sorting,
      userResponseDataPagination.size,
    );
    const dataToSend = {
      ...requestBody,
      search: escapeSearchQuery(searchString),
      isFrRoute: false,
    };
    getAllUserResponsesItemsForExport(
      dataToSend,
      locationId,
      frRoute,
      createDateFrom,
      createDateTo,
    );
  };

  const updatedRecord = (items) => {
    return items.map((item) => {
      return {
        ...item,
        orderDate: UTCToDateFormat(item.orderDate),
        surveyCompletionDate: UTCToDateTimeFormatWithoutSeconds(
          item.surveyCompletionDate,
        ),
        averageGuestTransaction: item.averageGuestTransaction,
      };
    });
  };

  const allConversationModal = (e, customerId, customerName) => {
    e.stopPropagation();
    setComponent(
      openConversationAllInternalNotesModal({
        customerId,
        closeModal,
        customerName,
        brandId: brandId || user?.brandId,
        locationId,
      }),
    );
  };

  //Get all user Responses
  useEffect(() => {
    let brandLocationId = getItemFromStorage("LocationId");
    if (!brandLocationId) {
      setItemToStorage("LocationId", locationId);
    }
    if (queryString || searchString) {
      getData(0, null, userResponseDataPagination.size);
    }
    // eslint-disable-next-line
  }, [searchString, queryString, selectedRatingOption]);

  useEffect(() => {
    setQueryString(
      buildSurveyCompletionDateFilterQuery({
        createDateFrom,
        createDateTo,
        starValue,
      }),
    );
    // eslint-disable-next-line
  }, [createDateFrom, createDateTo, starValue]);

  const storePerformanceColumns = useMemo(
    () =>
      individualStorePerformanceColumns(
        isBrandInboxActive,
        allConversationModal,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBrandInboxActive],
  );

  const responsesStatusTitle = getStarResponsesStatusTitle(selectedOption, {
    allLabel: FILTER_LABEL.ALL,
    allResponsesLabel: PERFORMANCE_STATUS_LABELS.TOTAL_RESPONSES,
    buildFilteredLabel: (options) =>
      `Total ${options.map((item) => item.value)} Star Responses`,
  });

  //Getting Survey Profile Data
  useEffect(() => {
    handleAsyncAction(
      frRoute
        ? getStoreProfileData(locationId, true)
        : getStoreProfileData(locationId, false),
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (allItemsForExport.length) {
      setExportData(updatedRecord(allItemsForExport));
      exportCSV();
      resetAllExportItems();
    }
    // eslint-disable-next-line
  }, [allItemsForExport]);

  return (
    <>
      {/* Display Modal */}
      {showModal && modalElement}
      {(spinner(ALL_USER_RESPONSE_DATA) ||
        spinner(GET_AVERAGE_RATING_RESPONSES)) && <Spinner />}
      <FilterComponent
        dateState={dateState}
        setMaxStar={setMaxStar}
        setMinStar={setMinStar}
        setDateState={setDateState}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        setStarValue={setStarValue}
        setDayCounts={setDayCounts}
        selectedRatingOption={selectedRatingOption}
        handleRatingOptionChange={handleRatingOptionChange}
        isStarSelectControlled
      />
      <div className="container-fluid py-3">
        <div className=" d-md-flex mb-3 justify-content-between">
          <StatusCard
            title={PERFORMANCE_STATUS_LABELS.TOTAL_CUSTOMERS}
            data={totalCustomers}
            classNames="me-3 mb-3 mb-md-0"
            bodyClassNames="p-3"
          />
          <StatusCard
            title={responsesStatusTitle}
            data={totalResponses}
            classNames=""
            bodyClassNames="p-3"
          />
        </div>
        <div className=" d-md-flex mb-3 justify-content-between mt-2">
          <AverageRatingCard
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="me-3 mb-3 mb-md-0"
            title={AVERAGE_TRANSACTION_RATING}
            averageRating={
              storesAvgSentimentAndTransactionRatingData?.averageTransactionRatings
            }
            previousAverageRating={
              storesAvgSentimentAndTransactionRatingData?.previousAverageTransactionRatings
            }
            averageRatingQuestionsData={
              storesAvgSentimentAndTransactionRatingData?.averageTransaction
            }
          />
          <AverageRatingCard
            dayCounts={dayCounts}
            bodyClassNames="px-3 pb-0"
            classNames="mb-md-0 mb-3"
            title={AVERAGE_SENTIMENT_RATING}
            averageRating={
              storesAvgSentimentAndTransactionRatingData?.averageSentimentRatings
            }
            previousAverageRating={
              storesAvgSentimentAndTransactionRatingData?.previousAverageSentimentRatings
            }
            averageRatingQuestionsData={
              storesAvgSentimentAndTransactionRatingData?.averageSentiment
                ? storesAvgSentimentAndTransactionRatingData.averageSentiment
                : []
            }
          />
        </div>
        <ExportButton
          exportFunc={getExportData}
          isDisabled={!userResponseDataPagination.totalElements}
        />
        <div className="notification-form card shadow">
          <div className="card-body px-0">
            <div className="d-flex justify-content-between">
              <h5 className=" responses px-4">
                {PERFORMANCE_STATUS_LABELS.CONSUMER_RESPONSES}
              </h5>
              <SearchComponent
                searchString={searchString}
                setSearchString={setSearchString}
              />
            </div>
            <div className="mt-3">
              <DataTable
                columns={storePerformanceColumns}
                data={userResponsesData}
                totalRows={userResponseDataPagination.totalElements}
                numberOfRowsPerPage={userResponseDataPagination.size}
                pageNumber={userResponseDataPagination.number + 1}
                pageSize={userResponseDataPagination.size}
                sortedBy={sorting}
                onRowClick={(e) => navigateToConsumerResponses(e)}
                onPageChange={(pageNum) =>
                  getData(pageNum, null, userResponseDataPagination.size)
                }
                onColumnSort={(sortBy) =>
                  getData(
                    userResponseDataPagination.number,
                    sortBy,
                    userResponseDataPagination.size,
                  )
                }
                onSizeChange={(size, pageNum) => {
                  getData(pageNum, null, size);
                }}
                isPaginationDropDownEnable={true}
              />
            </div>
          </div>
        </div>
      </div>
      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        headers={INDIVIDUAL_STORE_PERFORMANCE_EXPORT_HEADERS}
        data={exportData}
        filename={`${fileName(
          `${componentName} (${profileData.name})`,
          createDateFrom,
          createDateTo,
        )}.csv`}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  systemReducer: state.system,
  authentication: state.authentication,
  profileData: state.individualStoreProfileReducer.profileData,
  individualStorePerformanceReducer: state.individualStorePerformanceReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getAllUserResponses: (body, locationId, frRoute, fromDate, toDate) =>
    dispatch(
      individualStorePerformanceActions.getAllUserResponses(
        body,
        locationId,
        frRoute,
        fromDate,
        toDate,
      ),
    ),
  getStoreProfileData: (locationId, fromFR) =>
    dispatch(
      individualStoreProfileActions.getStoreProfileData(locationId, fromFR),
    ),
  getAllUserResponsesItemsForExport: (
    body,
    locationId,
    frRoute,
    fromDate,
    toDate,
  ) =>
    dispatch(
      individualStorePerformanceActions.getAllUserResponsesItemsForExport(
        body,
        locationId,
        frRoute,
        fromDate,
        toDate,
      ),
    ),
  resetAllExportItems: () =>
    dispatch(individualStorePerformanceActions.resetAllExportItems()),

  getCustomerConversations: (customerId) =>
    dispatch(
      individualStorePerformanceActions.getCustomerConversations(customerId),
    ),
  getAverageRatingResponses: (payload) =>
    dispatch(brandPerformanceActions.getAverageRatingResponses(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(IndividualStorePerformance);
